/**
 * Unauthenticated route tree.
 *
 * ``/login``, ``/signup``, ``/reset-request`` render the matching
 * screen. Each screen's "switch to X" callbacks become ``navigate``
 * calls. Any other path under the unauthenticated branch redirects
 * to ``/login`` and preserves the original location via ``?next=``.
 *
 * The Google-OAuth-finish and password-reset-confirm pages do NOT
 * live here — they are siblings of this component at the top of the
 * router tree because they must render regardless of auth state
 * (Google finish completes the sign-in mid-render; reset confirm
 * runs from an email link without a session).
 */

import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { LoginScreen } from "./LoginScreen";
import { PasswordResetRequestScreen } from "./PasswordResetRequestScreen";
import { SignupScreen } from "./SignupScreen";

export function AuthRoutes(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") ?? "/today";

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <LoginScreen
            onSwitchToSignup={() =>
              navigate(`/signup?next=${encodeURIComponent(next)}`)
            }
            onForgotPassword={() => navigate("/reset-request")}
          />
        }
      />
      <Route
        path="/signup"
        element={
          <SignupScreen onSwitchToLogin={() => navigate("/login")} />
        }
      />
      <Route
        path="/reset-request"
        element={
          <PasswordResetRequestScreen
            onBackToLogin={() => navigate("/login")}
          />
        }
      />
      <Route
        path="*"
        element={
          <Navigate
            to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
            replace
          />
        }
      />
    </Routes>
  );
}
